import { getBaseRole } from '@/helpers/customRoles';
import {
  SupportTicketSection,
  SupportTicketState,
  SupportTicketStatus,
  SupportTicketSummary,
  SupportTicketThread,
  supportTicketApi,
} from '@/api/queries/supportTicket'
import { selectProfile } from '@store/profile.reducer'
import websocket from '@/utils/websocket'
import PlatformSupportLauncher, { PlatformSupportContext } from './PlatformSupportLauncher'
import SupportAnnouncement from './SupportAnnouncement'
import classNames from 'classnames'
import { Tooltip } from 'antd'
import { FC, FormEvent, Fragment, KeyboardEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import classes from './index.module.css'

type View =
  | { name: 'home' }
  | { name: 'compose'; section: SupportTicketSection }
  | { name: 'thread'; id: number }

interface Props {
  className?: string
  children?: ReactNode
  enabled?: boolean
}

const SUPPORT_ROLES = ['admin', 'junior-admin', 'seminarian', 'senior-seminarian']
const MAX_MESSAGE = 5000
const MAX_IMAGES = 5
const MAX_IMAGE_BYTES = 10 * 1024 * 1024
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

type Draft = { file: File; preview: string }
type TicketStatusFilter = SupportTicketStatus | 'all' | 'not_closed'

const statusLabels: Record<SupportTicketStatus, string> = {
  open: 'Ждёт ответа',
  answered: 'Есть ответ',
  closed: 'Закрыто',
}

const formatTime = (timestamp: number) =>
  new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }).format(new Date(timestamp * 1000))

const formatTaskPrice = (price: string) => new Intl.NumberFormat('ru-RU', {
  style: 'currency', currency: 'RUB', maximumFractionDigits: 2,
}).format(Number(price) || 0)

const formatTaskDate = (date: string) => {
  const [year, month, day] = date.split('-')
  return year && month && day ? `${day}.${month}.${year}` : date
}

// Render plain HTTPS links without interpreting any HTML sent in a message.
const messageWithLinks = (body: string) => body.split(/(https:\/\/[^\s<>"']+)/gi).map((part, index) => {
  if (!/^https:\/\//i.test(part)) return part
  const url = part.replace(/[.,!?;:)\]»]+$/, '')
  try {
    if (!new URL(url).hostname) return part
  } catch {
    return part
  }
  return <Fragment key={index}><a href={url} target="_blank" rel="noopener noreferrer">{url}</a>{part.slice(url.length)}</Fragment>
})

/** Interceptor resolves 4xx with the axios error instead of rejecting: read both shapes. */
const unwrap = <T,>(response: any): T => {
  if (response?.data?.status === 'success') return response.data.data as T
  const error = response?.response?.data?.error ?? response?.data?.error
  throw new Error(Array.isArray(error) ? String(error[0] ?? '') : typeof error === 'string' ? error : '')
}

const icon = (paths: ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {paths}
  </svg>
)

const sectionIcon = (name: string) => {
  const lower = name.toLowerCase()
  if (lower.includes('финанс') || lower.includes('оплат') || lower.includes('счёт') || lower.includes('счет')) {
    return icon(<><rect x="3" y="6" width="18" height="13" rx="2.5" /><path d="M3 10h18M16 15h2" /></>)
  }
  if (lower.includes('доработ') || lower.includes('идея') || lower.includes('предлож')) {
    return icon(<><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 0 0-3.6 10.8c.6.5 1 1.2 1.1 2h5c.1-.8.5-1.5 1.1-2A6 6 0 0 0 12 3Z" /></>)
  }
  return icon(<><circle cx="12" cy="12" r="9" /><path d="M9.6 9.3a2.5 2.5 0 1 1 3.4 2.3c-.6.3-1 .8-1 1.5v.4M12 16.8h.01" /></>)
}

const PlatformSupport: FC<Props> = ({ className, children, enabled = true }) => {
  const profile = useSelector(selectProfile) as { id?: number | string; role?: string; roles?: string[] } | undefined
  const canUseSupport = enabled && Boolean(
    SUPPORT_ROLES.includes(getBaseRole(profile?.role ?? '')) ||
    profile?.roles?.some?.((role) => SUPPORT_ROLES.includes(getBaseRole(role))),
  )
  const [state, setState] = useState<SupportTicketState | null>(null)
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [view, setView] = useState<View>({ name: 'home' })
  const [tickets, setTickets] = useState<SupportTicketSummary[] | null>(null)
  const [ticketStatusFilter, setTicketStatusFilter] = useState<TicketStatusFilter>('not_closed')
  const [ticketSectionFilter, setTicketSectionFilter] = useState('all')
  const [thread, setThread] = useState<SupportTicketThread | null>(null)
  const [draft, setDraft] = useState('')
  const [images, setImages] = useState<Draft[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [sending, setSending] = useState(false)
  const [escalating, setEscalating] = useState(false)
  const [closing, setClosing] = useState(false)
  const [requestingCallback, setRequestingCallback] = useState(false)
  const callbackRequest = useRef(false)
  const [taskDecision, setTaskDecision] = useState<'accepted' | 'declined' | null>(null)
  const [error, setError] = useState('')
  const [specialistNotice, setSpecialistNotice] = useState(false)
  const refreshStateRef = useRef<() => void>(() => {})
  const messagesRef = useRef<HTMLDivElement>(null)
  const threadRequest = useRef(0)
  const viewRef = useRef(view)
  viewRef.current = view
  const openRef = useRef(open)
  openRef.current = open
  const callbackAvailableRef = useRef(false)
  callbackAvailableRef.current = Boolean(state?.callback)

  // Ответы поддержки приходят по вебсокету (platform_support.updated); опрос — только страховка
  // на случай обрыва сокета: раз в минуту без сокета, раз в 5 минут с ним, и при возврате на вкладку
  useEffect(() => {
    setState(null)
    setOpen(false)
    setTickets(null)
    setTicketStatusFilter('not_closed')
    setTicketSectionFilter('all')
    setThread(null)
    setTaskDecision(null)
    setView({ name: 'home' })
    ++threadRequest.current
    if (!canUseSupport || !profile?.id) return undefined
    let disposed = false
    let loading = false
    let lastCheck = 0
    const check = async (force = false) => {
      if (disposed || loading || document.hidden || (!force && Date.now() - lastCheck < 10000)) return
      loading = true
      lastCheck = Date.now()
      try {
        const next = unwrap<SupportTicketState>(await supportTicketApi.state())
        if (!disposed) setState(next)
      } catch {
        // Следующая попытка через минуту: сбой не должен мешать работе платформы
      } finally {
        loading = false
      }
    }
    refreshStateRef.current = () => { void check(true) }
    void check()
    const onActivity = () => { void check() }
    const interval = window.setInterval(() => {
      if (!websocket.isOpen() || Date.now() - lastCheck >= 300000) void check()
    }, 60000)
    // После переподключения сокета подтягиваем то, что могли пропустить
    const unsubscribeOpen = websocket.subscribeOpen(() => { void check(true) })
    window.addEventListener('focus', onActivity)
    document.addEventListener('visibilitychange', onActivity)
    return () => {
      disposed = true
      refreshStateRef.current = () => {}
      unsubscribeOpen?.()
      window.clearInterval(interval)
      window.removeEventListener('focus', onActivity)
      document.removeEventListener('visibilitychange', onActivity)
    }
  }, [canUseSupport, profile?.id, profile?.role])

  const available = Boolean(canUseSupport && state?.available)
  const announcementEnabled = available && !state?.aiOnly && Boolean(state?.announcement?.enabled)
    && Boolean(['admin', 'junior-admin'].includes(getBaseRole(profile?.role ?? ''))
      || profile?.roles?.some?.((role) => ['admin', 'junior-admin'].includes(getBaseRole(role))))
  const onAnnouncementClaimed = useCallback(() => {
    setState((current) => current ? {
      ...current,
      announcement: { enabled: Boolean(current.announcement?.enabled), pending: false },
    } : current)
  }, [])

  useEffect(() => {
    if (!available) setOpen(false)
  }, [available])

  const loadTickets = useCallback(async () => {
    try {
      setTickets(unwrap<SupportTicketSummary[]>(await supportTicketApi.list()))
    } catch {
      setTickets((current) => current ?? [])
    }
  }, [])

  const loadThread = useCallback(async (id: number) => {
    const request = ++threadRequest.current
    try {
      const next = unwrap<SupportTicketThread>(await supportTicketApi.view(id))
      const current = viewRef.current
      if (request === threadRequest.current && current.name === 'thread' && current.id === id) {
        setThread(next)
        refreshStateRef.current()
      }
    } catch (loadError) {
      const current = viewRef.current
      if (request === threadRequest.current && current.name === 'thread' && current.id === id) {
        setError((loadError as Error).message || 'Не удалось загрузить обращение. Проверьте соединение.')
      }
    }
  }, [])

  // Ответ поддержки или смена статуса: сервер шлёт событие только этому администратору
  useEffect(() => {
    if (!canUseSupport || !profile?.id) return undefined
    const unsubscribe = websocket.subscribe('platform_support.updated', (message: { data?: { ticket_id?: number } }) => {
      refreshStateRef.current()
      if (!openRef.current) return
      const current = viewRef.current
      const ticketId = Number(message?.data?.ticket_id)
      if (current.name === 'thread' && current.id === ticketId) void loadThread(ticketId)
      else if (current.name === 'home') void loadTickets()
    })
    return () => { unsubscribe?.() }
  }, [canUseSupport, profile?.id, loadThread, loadTickets])

  // При открытии загружаем данные; сокет обновляет сразу, опрос страхует пропущенные события
  useEffect(() => {
    if (!open || view.name === 'compose') return undefined
    const refresh = () => {
      if (document.hidden) return
      if (callbackAvailableRef.current) refreshStateRef.current()
      if (view.name === 'thread') void loadThread(view.id)
      else void loadTickets()
    }
    refresh()
    // A connected socket can still miss an event (worker restart, Redis outage).
    // Only the visible chat is checked; reconnect/focus also recovers missed answers.
    const interval = window.setInterval(refresh, view.name === 'thread' ? 15000 : 30000)
    const unsubscribeOpen = websocket.subscribeOpen(refresh)
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.clearInterval(interval)
      unsubscribeOpen?.()
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [open, view, loadThread, loadTickets])

  useEffect(() => {
    const list = messagesRef.current
    if (list) list.scrollTop = list.scrollHeight
  }, [thread?.id, thread?.messages.length])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const requestCallback = async () => {
    if (callbackRequest.current || !state?.callback || state.callback.pending || state.callback.remaining <= 0) return
    callbackRequest.current = true
    setRequestingCallback(true)
    setError('')
    try {
      const callback = unwrap<NonNullable<SupportTicketState['callback']>>(await supportTicketApi.requestCallback())
      setState((current) => current ? { ...current, callback } : current)
      void loadTickets()
    } catch (failure) {
      setError((failure as Error).message || 'Не удалось запросить звонок. Проверьте соединение и попробуйте ещё раз.')
    } finally {
      callbackRequest.current = false
      setRequestingCallback(false)
      refreshStateRef.current()
    }
  }

  const clearImages = () => {
    setImages((current) => {
      current.forEach((image) => URL.revokeObjectURL(image.preview))
      return []
    })
  }

  // Скрепка, вставка из буфера (Ctrl+V скриншота) и перетаскивание ведут сюда
  const addImages = (list: FileList | File[] | null) => {
    if (replyBlockReason || escalating || closing) return
    const files = Array.from(list ?? [])
    if (!files.length) return
    const accepted = files.filter((file) => IMAGE_TYPES.includes(file.type) && file.size <= MAX_IMAGE_BYTES)
    const room = MAX_IMAGES - images.length
    const added = accepted.slice(0, Math.max(0, room))
    if (accepted.length < files.length) {
      setError('Можно прикладывать изображения JPG, PNG, WEBP и GIF до 10 МБ.')
    } else if (added.length < accepted.length) {
      setError(`Можно приложить не больше ${MAX_IMAGES} изображений.`)
    } else {
      setError('')
    }
    if (added.length) {
      setImages((current) => [...current, ...added.map((file) => ({ file, preview: URL.createObjectURL(file) }))])
    }
  }

  const removeImage = (index: number) => {
    setImages((current) => {
      URL.revokeObjectURL(current[index].preview)
      return current.filter((_, position) => position !== index)
    })
  }

  useEffect(() => () => {
    setImages((current) => {
      current.forEach((image) => URL.revokeObjectURL(image.preview))
      return current
    })
  }, [])

  const go = (next: View) => {
    setSpecialistNotice(false)
    setTaskDecision(null)
    setView(next)
    setDraft('')
    clearImages()
    setError('')
    if (next.name !== 'thread') setThread(null)
  }

  const submit = async (event?: FormEvent) => {
    event?.preventDefault()
    const message = draft.trim()
    if ((!message && !images.length) || sending || escalating || closing || replyBlockReason || view.name === 'home') return
    setSending(true)
    setError('')
    const files = images.map((image) => image.file)
    try {
      const response = view.name === 'compose'
        ? await supportTicketApi.create(view.section.id, message, files)
        : await supportTicketApi.reply(view.id, message, files)
      const next = unwrap<SupportTicketThread>(response)
      ++threadRequest.current
      setDraft('')
      clearImages()
      setThread(next)
      setView({ name: 'thread', id: next.id })
      void loadTickets()
    } catch (submitError) {
      setError((submitError as Error).message || 'Не удалось отправить сообщение. Проверьте соединение и попробуйте ещё раз.')
    } finally {
      setSending(false)
    }
  }

  // «Связь со специалистом»: ИИ-ассистент больше не отвечает, обращение уходит команде
  const escalate = async () => {
    if (!thread || escalating) return
    setEscalating(true)
    setError('')
    try {
      const next = unwrap<SupportTicketThread>(await supportTicketApi.escalate(thread.id))
      ++threadRequest.current
      setThread(next)
      if (state?.aiOnly || thread.aiOnly) setSpecialistNotice(true)
      void loadTickets()
    } catch (escalateError) {
      setError((escalateError as Error).message || 'Не удалось позвать специалиста. Попробуйте ещё раз.')
    } finally {
      setEscalating(false)
    }
  }

  const resolve = async () => {
    if (!thread || closing || escalating || sending) return
    setClosing(true)
    setError('')
    try {
      const next = unwrap<SupportTicketThread>(await supportTicketApi.close(thread.id))
      ++threadRequest.current
      setThread(next)
      refreshStateRef.current()
      void loadTickets()
    } catch (closeError) {
      setError((closeError as Error).message || 'Не удалось закрыть обращение. Попробуйте ещё раз.')
    } finally {
      setClosing(false)
    }
  }

  const decideTask = async (decision: 'accepted' | 'declined') => {
    if (!thread || taskDecision) return
    setTaskDecision(decision)
    setError('')
    try {
      const next = unwrap<SupportTicketThread>(await supportTicketApi.decideTask(thread.id, decision))
      ++threadRequest.current
      setThread(next)
      refreshStateRef.current()
      void loadTickets()
    } catch (decisionError) {
      setError((decisionError as Error).message || 'Не удалось сохранить решение по техническому заданию.')
    } finally {
      setTaskDecision(null)
    }
  }

  const onDraftKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      void submit()
    }
  }

  if (!available || !state) {
    // Keep the page mounted while support availability is loading or changing.
    return (
      <PlatformSupportContext.Provider value={null}>
        <Fragment key="content">{children}</Fragment>
      </PlatformSupportContext.Provider>
    )
  }

  const title = view.name === 'compose' ? view.section.name
    : view.name === 'thread' ? (thread ? `Обращение #${thread.id}` : 'Обращение') : 'Техподдержка'

  const texts = view.name === 'compose' ? view.section.texts : thread?.texts
  const hours = view.name === 'thread' ? thread?.workingHours ?? state.workingHours : state.workingHours
  const ai = view.name === 'compose' ? view.section.ai : thread?.handler === 'ai'
  const lastClientId = Math.max(0, ...(thread?.messages.filter((message) => !message.fromSupport).map((message) => message.id) ?? []))
  const latestAiReply = thread?.messages.filter((message) => message.fromAi && message.id > lastClientId)
    .reduce<SupportTicketThread['messages'][number] | undefined>((latest, message) => !latest || message.id > latest.id ? message : latest, undefined)
  const aiAnswered = thread?.aiAnswered ?? Boolean(latestAiReply && latestAiReply.isQuestion !== false)
  const callbackClosed = view.name === 'thread' && thread?.isCallback && thread.status === 'closed'
  const replyBlockReason = callbackClosed ? 'callback_closed' : view.name === 'thread' && thread?.status !== 'closed'
    ? thread?.replyBlockReason !== undefined ? thread.replyBlockReason
      : thread?.handler === 'ai' ? aiAnswered ? 'ai_decision' : latestAiReply ? null : 'ai_pending' : null
    : null
  const aiPending = replyBlockReason === 'ai_pending'
  const replyBlockText = callbackClosed
    ? 'Звонок завершён. Новый звонок можно запросить на главном экране техподдержки.'
    : replyBlockReason === 'ai_pending'
    ? 'Дождитесь ответа ИИ-ассистента.'
    : replyBlockReason === 'ai_decision'
      ? 'Выберите «Все понятно» или «Связь со специалистом», чтобы продолжить.'
      : 'Специалист подключится к обращению. После его первого ответа вы сможете написать сообщение.'
  const offlineText = ai
    ? texts?.offlineAi ?? 'Сейчас сотрудники поддержки не работают. Вам ответит ИИ-ассистент. Специалист подключится в рабочее время.'
    : texts?.offline ?? 'Сейчас сотрудники поддержки не работают. Ваше обращение сохранено. Мы ответим в рабочее время.'
  const greeting = ai ? texts?.aiGreeting : texts?.greeting
  const offlineNote = !state.aiOnly && !thread?.aiOnly && hours?.enabled && !hours.open && offlineText !== '' && (
    <div className={classes.offlineNote} role="status">
      <span>{offlineText}</span>
      <small>{hours.label}</small>
    </div>
  )
  const callback = !state.aiOnly ? state.callback : null
  const callbackLabel = requestingCallback ? 'Отправляем запрос…' : callback?.pending
    ? 'Вы запросили звонок' : callback?.limit === 0 ? 'Звонки недоступны'
      : callback?.remaining === 0 ? 'Лимит звонков исчерпан' : 'Заказать звонок специалиста'
  const callbackHint = callback
    ? `У вас как у партнёра Импульса бесплатных звонков в месяц: ${callback.limit}. Осталось: ${callback.remaining}. Лимит обновляется 1-го числа по Москве. Повторный звонок можно запросить после закрытия текущего обращения менеджером.`
    : ''
  const ticketSections = Array.from(new Set((tickets ?? []).map((ticket) => ticket.section)))
    .sort((left, right) => left.localeCompare(right, 'ru'))
  const visibleTickets = (tickets ?? []).filter((ticket) => (
    (ticketStatusFilter === 'all'
      || (ticketStatusFilter === 'not_closed' ? ticket.status !== 'closed' : ticket.status === ticketStatusFilter))
    && (ticketSectionFilter === 'all' || ticket.section === ticketSectionFilter)
  ))

  const composer = (placeholder: string, button: string) => (
    <form
      className={classes.composer}
      onSubmit={submit}
      onDragOver={(event) => { if (event.dataTransfer?.types?.includes('Files')) event.preventDefault() }}
      onDrop={(event) => {
        if (!event.dataTransfer?.files?.length) return
        event.preventDefault()
        addImages(event.dataTransfer.files)
      }}
    >
      <textarea
        className={classes.textarea}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onDraftKeyDown}
        onPaste={(event) => {
          const pasted = Array.from(event.clipboardData?.files ?? []).filter((file) => file.type.startsWith('image/'))
          if (!pasted.length) return
          event.preventDefault()
          addImages(pasted)
        }}
        maxLength={MAX_MESSAGE}
        rows={view.name === 'compose' ? 6 : 3}
        placeholder={placeholder}
        aria-label={placeholder}
        disabled={sending || escalating || closing || Boolean(replyBlockReason)}
        aria-describedby={replyBlockReason ? 'support-reply-blocked' : undefined}
        autoFocus
      />
      {images.length > 0 && (
        <ul className={classes.previews} aria-label="Приложенные изображения">
          {images.map((image, index) => (
            <li key={image.preview}>
              <img src={image.preview} alt={image.file.name} />
              <button type="button" onClick={() => removeImage(index)} aria-label={`Убрать ${image.file.name}`} disabled={sending}>×</button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className={classes.error} role="alert">{error}</p>}
      {replyBlockReason && <p id="support-reply-blocked" className={classes.hint} role="status">{replyBlockText}</p>}
      <div className={classes.composerFooter}>
        <input
          ref={fileInputRef}
          type="file"
          accept={IMAGE_TYPES.join(',')}
          multiple
          hidden
          onChange={(event) => {
            addImages(event.target.files)
            event.target.value = ''
          }}
        />
        <button
          type="button"
          className={classes.attach}
          onClick={() => fileInputRef.current?.click()}
          disabled={sending || escalating || closing || Boolean(replyBlockReason) || images.length >= MAX_IMAGES}
          aria-label="Приложить изображение"
          title="Приложить изображение (можно вставить скриншот через Ctrl + V)"
        >
          {icon(<path d="m21 11.5-8.6 8.6a5.5 5.5 0 0 1-7.8-7.8l8.6-8.6a3.7 3.7 0 0 1 5.2 5.2l-8.6 8.6a1.8 1.8 0 0 1-2.6-2.6l8-8" />)}
        </button>
        {!replyBlockReason && <span className={classes.hint}>Ctrl + Enter — отправить</span>}
        <button type="submit" className={classes.send} disabled={sending || escalating || closing || Boolean(replyBlockReason) || (!draft.trim() && !images.length)}>
          {sending ? 'Отправляем…' : button}
        </button>
      </div>
    </form>
  )

  const panel = (
    <section className={classNames(classes.panel, expanded && classes.expanded)} role="dialog" aria-label="Техподдержка платформы">
      <header className={classes.header}>
        {view.name !== 'home' && (
          <button type="button" className={classes.iconButton} onClick={() => go({ name: 'home' })} aria-label="Назад">
            {icon(<path d="m15 18-6-6 6-6" />)}
          </button>
        )}
        <div className={classes.headerText}>
          <h2>{title}</h2>
          <span>
            {view.name === 'thread' && thread
              ? `${thread.section} · ${statusLabels[thread.status]}`
              : state.aiOnly ? 'На ваши вопросы отвечает ИИ-ассистент' : 'Команда Импульс ЛМС ответит здесь'}
          </span>
        </div>
        <Tooltip title={expanded ? 'Свернуть окно' : 'Развернуть окно'}>
          <button type="button" className={classes.iconButton} onClick={() => setExpanded((value) => !value)} aria-label={expanded ? 'Свернуть окно' : 'Развернуть окно'} aria-pressed={expanded}>
            {expanded
              ? icon(<path d="M4 9h5V4M20 15h-5v5M9 9 3 3m12 12 6 6" />)
              : icon(<path d="M9 3H3v6m12 12h6v-6M3 3l7 7m11 11-7-7" />)}
          </button>
        </Tooltip>
        <button type="button" className={classes.iconButton} onClick={() => setOpen(false)} aria-label="Закрыть">
          {icon(<path d="M6 6l12 12M18 6 6 18" />)}
        </button>
      </header>

      {view.name === 'home' && (
        <div className={classes.body}>
          <p className={classes.lead}>Выберите раздел, и мы ответим вам здесь.</p>
          {state.sections.length > 0 || callback ? (
            <div className={classes.sections}>
              {state.sections.map((section) => (
                <button key={section.id} type="button" className={classes.section} onClick={() => go({ name: 'compose', section })}>
                  <span className={classes.sectionIcon}>{sectionIcon(section.name)}</span>
                  <span className={classes.sectionName}>{section.name}</span>
                  <span className={classes.chevron}>{icon(<path d="m9 6 6 6-6 6" />)}</span>
                </button>
              ))}
              {callback && (
                <div className={classes.callback}>
                  <button type="button" className={classes.callbackButton}
                    disabled={requestingCallback || callback.pending || callback.remaining <= 0}
                    aria-busy={requestingCallback} onClick={() => void requestCallback()}>
                    <span className={classes.sectionIcon}>{icon(<path d="M7 3H4a1 1 0 0 0-1 1c0 9.4 7.6 17 17 17a1 1 0 0 0 1-1v-3l-5-2-2 2a14 14 0 0 1-7-7l2-2-2-5Z" />)}</span>
                    <span className={classes.sectionName}>{callbackLabel}<small>
                      {callback.pending ? 'Ожидайте связи со специалистом' : callback.limit === 0
                        ? 'Для вашей платформы звонки не подключены' : callback.remaining > 0
                        ? `Осталось ${callback.remaining} из ${callback.limit} в этом месяце`
                        : `Доступно с ${new Date(callback.nextResetAt * 1000).toLocaleDateString('ru-RU', { timeZone: 'Europe/Moscow', day: 'numeric', month: 'long' })}`}
                    </small></span>
                  </button>
                  <Tooltip title={callbackHint} placement="top">
                    <button type="button" className={classes.callbackInfo} aria-label={callbackHint}>i</button>
                  </Tooltip>
                </div>
              )}
            </div>
          ) : (
            <p className={classes.empty}>{state.aiOnly ? 'ИИ-ассистент сейчас недоступен. Обратитесь к руководству вашей платформы.' : 'Разделы обращений пока не настроены.'}</p>
          )}
          {error && <p className={classes.error} role="alert">{error}</p>}
          {tickets && tickets.length > 0 && (
            <>
              <div className={classes.ticketsHeader}>
                <h3 className={classes.subtitle}>Мои обращения</h3>
                <div className={classes.ticketFilters}>
                  <select
                    className={classes.ticketFilter}
                    aria-label="Фильтр обращений по статусу"
                    value={ticketStatusFilter}
                    onChange={(event) => setTicketStatusFilter(event.target.value as TicketStatusFilter)}
                  >
                    <option value="not_closed">Кроме закрытых</option>
                    <option value="all">Все статусы</option>
                    <option value="answered">Есть ответ</option>
                    <option value="closed">Закрыто</option>
                    <option value="open">Ждёт ответа</option>
                  </select>
                  <select
                    className={classes.ticketFilter}
                    aria-label="Фильтр обращений по разделу"
                    value={ticketSectionFilter}
                    onChange={(event) => setTicketSectionFilter(event.target.value)}
                  >
                    <option value="all">Все разделы</option>
                    {ticketSections.map((section) => <option key={section} value={section}>{section}</option>)}
                  </select>
                </div>
              </div>
              {visibleTickets.length > 0 ? (
                <ul className={classes.tickets}>
                  {visibleTickets.map((ticket) => (
                    <li key={ticket.id}>
                      <button type="button" className={classes.ticket} onClick={() => go({ name: 'thread', id: ticket.id })}>
                        <span className={classes.ticketTop}>
                          <b>#{ticket.id} · {ticket.section}</b>
                          <span className={classNames(classes.status, classes[`status_${ticket.status}`])}>{statusLabels[ticket.status]}</span>
                        </span>
                        <span className={classes.ticketPreview}>
                          {ticket.unread && <i className={classes.unreadDot} aria-label="Новый ответ" />}
                          {ticket.lastMessage.fromSupport ? 'Поддержка: ' : 'Вы: '}{ticket.lastMessage.body || '📎 Изображение'}
                        </span>
                        <time className={classes.ticketTime}>{formatTime(ticket.updatedAt)}</time>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : <p className={classes.filterEmpty}>По выбранным фильтрам обращений нет.</p>}
            </>
          )}
        </div>
      )}

      {view.name === 'compose' && (
        <div className={classes.body}>
          {texts?.lead !== '' && <p className={classes.lead}>{texts?.lead ?? 'Опишите вопрос подробно: что делали, что ожидали и что получилось. Ответ придёт в это окно.'}</p>}
          {view.section.ai && texts?.aiLead !== '' && (
            <p className={classes.aiLead}>🤖 {state.aiOnly ? 'На вопросы преподавателей отвечает ИИ-ассистент. Если нужна помощь специалиста, обратитесь к руководству вашей платформы.' : texts?.aiLead ?? 'Сначала ответит ИИ-ассистент. Если он не поможет — в переписке будет кнопка «Связь со специалистом».'}</p>
          )}
          {offlineNote}
          {composer('Ваше сообщение…', 'Отправить')}
        </div>
      )}

      {view.name === 'thread' && (
        <>
          <div ref={messagesRef} className={classes.messages} aria-live="polite">
            {!thread && !error && <p className={classes.empty}>Загружаем…</p>}
            {thread && greeting && <p className={classes.greeting}>{greeting}</p>}
            {thread?.messages.map((message) => (
              <article key={message.id} className={classNames(classes.message, message.fromSupport ? classes.fromSupport : classes.fromMe)}>
                <header>
                  <b>{!message.fromSupport ? 'Вы' : message.fromAi ? '🤖 ИИ-ассистент' : 'Техподдержка'}</b>
                  {message.fromSupport && !message.fromAi && message.authorName && <span>{message.authorName}</span>}
                  <time>{formatTime(message.createdAt)}</time>
                </header>
                {message.body && <div className={classes.messageBody}>{messageWithLinks(message.body)}</div>}
                {message.taskProposal && (
                  <section className={classes.taskCard} aria-label={`Техническое задание: ${message.taskProposal.title}`}>
                    <span className={classes.taskEyebrow}>Согласованная задача</span>
                    <h3>{message.taskProposal.title}</h3>
                    <p className={classes.taskDescription}>{message.taskProposal.description}</p>
                    <dl className={classes.taskMeta}>
                      <div><dt>Стоимость</dt><dd>{formatTaskPrice(message.taskProposal.price)}</dd></div>
                      <div><dt>Примерный дедлайн</dt><dd>{formatTaskDate(message.taskProposal.deadline)}</dd></div>
                      <div><dt>Оплата</dt><dd>{message.taskProposal.paymentStatus === 'paid' ? 'Оплачено' : 'Не оплачено'}</dd></div>
                      <div><dt>Выполнение</dt><dd>{message.taskProposal.workStatus === 'completed' ? 'Выполнено' : 'В работе'}</dd></div>
                    </dl>
                    {message.taskProposal.decisionStatus === 'pending' ? (
                      <div className={classes.taskActions}>
                        <button type="button" className={classes.taskAccept} disabled={Boolean(taskDecision)}
                          onClick={() => { void decideTask('accepted') }}>
                          {taskDecision === 'accepted' ? 'Принимаем…' : 'Принять ТЗ'}
                        </button>
                        <button type="button" className={classes.taskDecline} disabled={Boolean(taskDecision)}
                          onClick={() => { void decideTask('declined') }}>
                          {taskDecision === 'declined' ? 'Отказываемся…' : 'Отказаться'}
                        </button>
                      </div>
                    ) : (
                      <strong className={classNames(classes.taskDecision, classes[`taskDecision_${message.taskProposal.decisionStatus}`])}>
                        {message.taskProposal.decisionStatus === 'accepted' ? 'ТЗ принято' : 'ТЗ отклонено'}
                      </strong>
                    )}
                  </section>
                )}
                {message.attachments && message.attachments.length > 0 && (
                  <div className={classes.files}>
                    {message.attachments.map((file) => (
                      <a key={file.url} href={file.url} target="_blank" rel="noopener noreferrer" title={file.name}>
                        <img src={file.url} alt={file.name} loading="lazy" />
                      </a>
                    ))}
                  </div>
                )}
              </article>
            ))}
            {aiPending && (
              <div className={classes.thinking} role="status">
                <span aria-hidden="true">🤖</span><span>ИИ-бот думает и готовит ответ</span>
                <span className={classes.dots} aria-hidden="true"><i /><i /><i /></span>
              </div>
            )}
          </div>
          {thread?.handler === 'ai' && thread.status !== 'closed' && aiAnswered && (
            <div className={classes.aiNote}>
              <button type="button" className={classes.send} onClick={() => { void resolve() }} disabled={closing || escalating || sending}>
                {closing ? 'Закрываем…' : 'Все понятно'}
              </button>
              <button type="button" className={classes.specialist} onClick={() => { void escalate() }} disabled={escalating || closing || sending}>
                {escalating ? 'Передаём вопрос…' : 'Связь со специалистом'}
              </button>
            </div>
          )}
          {thread?.handler === 'human' && thread.escalated && thread.status !== 'closed' && texts?.escalated !== '' && (
            <p className={classes.closedNote}>🙋 {texts?.escalated ?? 'Вопрос передан специалисту — он ответит здесь.'}</p>
          )}
          {specialistNotice && <p className={classes.closedNote} role="status">{state.specialistNotice ?? 'Свяжитесь с вашим руководством, чтобы передать вопрос специалистам поддержки.'}</p>}
          {thread?.status === 'closed' && !callbackClosed && texts?.closed !== '' && (
            <p className={classes.closedNote}>{texts?.closed ?? 'Обращение закрыто. Если вопрос остался — напишите, и мы откроем его снова.'}</p>
          )}
          {thread && offlineNote}
          {composer('Написать сообщение…', 'Отправить')}
        </>
      )}
    </section>
  )

  const toggle = () => {
    if (!open) go({ name: 'home' })
    setOpen((value) => !value)
  }

  const openSupport = () => {
    if (!open) go({ name: 'home' })
    setOpen(true)
  }

  return (
    <PlatformSupportContext.Provider value={{ open, unread: state.unread, toggle, openSupport, announcementEnabled }}>
      <Fragment key="content">{children === undefined ? <PlatformSupportLauncher className={className} /> : children}</Fragment>
      {open && createPortal(panel, document.body)}
      <SupportAnnouncement
        profileId={profile!.id!}
        enabled={announcementEnabled}
        pending={Boolean(state.announcement?.pending)}
        blocked={open}
        onClaimed={onAnnouncementClaimed}
        onOpenSupport={openSupport}
      />
    </PlatformSupportContext.Provider>
  )
}

export default PlatformSupport
